import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Avatar } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import useTheme from "../../../../common/hooks/useTheme";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import { getBreadcrumbItems } from "../navigation/navigationStructure";
import useBackNavigation from "../navigation/useBackNavigation";
import { useSidebar } from "../navigation/SidebarContext";
import { Theme } from "../../../../providers/ThemeProvider";
import AiSummaryFeature from "../../../chat/AiSummaryFeature";
import {
  appendChatModeToPath,
  resolveAiSummaryButtonOverride,
} from "../../../chat/chatMode";
import ArrowLeft from "../../svg/ArrowLeft";
import UserAccountMenu from "./UserAccountMenu";

/**
 * Which shell is this top bar rendered in?
 * - affiliate: the main tab — plain trail; no top-right avatar (the logged-in
 *   affiliate's avatar and account menu live at the bottom of the sidebar)
 * - client: a client workspace tab — the CLIENT's name/avatar shows at the
 *   top right as a static chip (no menu; the affiliate's account menu is in
 *   the sidebar). One client per tab, so no switching.
 * - self: the customer's own workspace — their avatar top right with the
 *   account menu, as always
 */
type BreadcrumbVariant = "affiliate" | "client" | "self";

interface BreadcrumbProps {
  variant: BreadcrumbVariant;
}

const Breadcrumb = ({ variant }: BreadcrumbProps) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const { ASSelectedClient } = useAppStore();
  const { AuthUser } = useAuth();
  const { sidebarWidth } = useSidebar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const showAiSummaryButton = resolveAiSummaryButtonOverride(location.search);

  // Detail pages get a back link to their listing here, so no page has to
  // render a back button of its own. Null on listing pages.
  const backNavigation = useBackNavigation();

  // Build breadcrumb trail from current path using centralized navigation structure
  const breadcrumbItems = useMemo(
    () => getBreadcrumbItems(location.pathname, variant === "self"),
    [location.pathname, variant]
  );

  // Get initials for a person shown top right (the logged-in customer in
  // self mode, the selected client in client mode)
  const getInitials = (first?: string, last?: string) =>
    `${first?.charAt(0) || ""}${last?.charAt(0) || ""}`.toUpperCase();

  return (
    <Box
      sx={{
        ...styles.topBarContainer(theme),
        minWidth: `${1440 - sidebarWidth}px`,
      }}
    >
      {/* Breadcrumb trail */}
      <Box sx={styles.breadcrumbTrail}>
        {backNavigation && (
          <>
            <Box
              sx={styles.backButton(theme)}
              onClick={(e) => {
                e.stopPropagation();
                backNavigation.goBack();
              }}
              role="button"
              aria-label="Back"
            >
              <ArrowLeft size={12} fill={theme.colors_ds.PRIMARY_ICON_MAIN} />
              Back
            </Box>
            <Box sx={styles.backDivider(theme)} />
          </>
        )}

        {breadcrumbItems.map((item, index) => {
          const isCurrentPage = index === breadcrumbItems.length - 1;

          return (
            <React.Fragment key={index}>
              <Box
                sx={styles.breadcrumbItem(theme, isCurrentPage, item)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.link && item.link !== "#") {
                    if (item.label === "Clients") {
                      // Back to the client list — this tab becomes an
                      // affiliate main tab (its client context is cleared by
                      // the AffiliateShell on mount).
                      navigate(
                        appendChatModeToPath(item.link, location.search)
                      );
                    } else {
                      // For any other breadcrumb item, check if we're on a detail page
                      const currentPath = location.pathname;
                      const itemPath = item.link;

                      // Check if current path is a detail page under this item
                      // (has additional segments after the item's path, like /prescription/123)
                      const isDetailPage = currentPath.startsWith(
                        itemPath + "/"
                      );

                      if (isDetailPage) {
                        // Navigate back to the base/list page (preserving query params)
                        const currentSearch = location.search;
                        navigate(itemPath + currentSearch);
                      }
                      // If already on the base/list page, do nothing
                    }
                  }
                }}
              >
                {item.label}
              </Box>

              {index < breadcrumbItems.length - 1 && (
                <KeyboardArrowRightIcon sx={styles.breadcrumbArrow(theme)} />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Top-right identity */}
      {variant === "self" && AuthUser.firstname && (
        // The logged-in customer — clickable, opens the account menu
        <Box
          sx={styles.userAvatarContainer}
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
        >
          <Avatar sx={styles.userAvatar(theme)}>
            {getInitials(AuthUser.firstname, AuthUser.lastname)}
          </Avatar>
          <Box sx={styles.userAvatarName(theme)}>
            {`${AuthUser.firstname || ""} ${AuthUser.lastname || ""}`.trim()}
          </Box>
          <KeyboardArrowDownIcon sx={styles.userAvatarArrow(theme)} />
        </Box>
      )}

      {variant === "client" && ASSelectedClient && (
        // The client this tab shows — static, like the simple-user view but
        // without the menu (the affiliate's menu is in the sidebar)
        <Box sx={styles.clientIdentity}>
          <Box sx={{ ...styles.userAvatarContainer, cursor: "default" }}>
            <Avatar sx={styles.userAvatar(theme)}>
              {getInitials(
                ASSelectedClient.firstName,
                ASSelectedClient.lastName
              )}
            </Avatar>
            <Box sx={styles.userAvatarName(theme)}>
              {`${ASSelectedClient.firstName || ""} ${
                ASSelectedClient.lastName || ""
              }`.trim()}
            </Box>
          </Box>
          {/* Behind ?aiSummary=1. It used to sit beside the inline client chip
              in the trail; that chip was the client switcher, which this
              architecture drops (one client per tab), so the button moves to
              the top-right chip that names the client instead. */}
          {showAiSummaryButton && (
            <AiSummaryFeature customerId={String(ASSelectedClient.id)} />
          )}
        </Box>
      )}

      {variant === "self" && (
        <UserAccountMenu
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
        />
      )}
    </Box>
  );
};

export default Breadcrumb;

const styles = {
  topBarContainer: (theme: Theme) => ({
    position: "sticky" as "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxShadow: "var(--box-shadow-general)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    height: "48px",
  }),
  breadcrumbTrail: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  // "← Back" (Figma node 16886:68186), shown only on detail pages — see
  // `useBackNavigation`. It leads the trail and is fenced off from it by
  // `backDivider`.
  backButton: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    cursor: "pointer",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    whiteSpace: "nowrap",
    "&:hover": {
      opacity: 0.8,
    },
  }),
  backDivider: (theme: Theme) => ({
    width: "1px",
    height: "23px",
    flexShrink: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
  }),
  breadcrumbItem: (theme: Theme, isCurrentPage: boolean, item: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: isCurrentPage
      ? theme.colors_ds.NEUTRAL_TEXT_STRONG
      : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    cursor: !isCurrentPage && item.link ? "pointer" : "default",
    "&:hover": item.link
      ? {
          color: isCurrentPage
            ? theme.colors_ds.NEUTRAL_TEXT_STRONG
            : theme.colors_ds.PRIMARY_TEXT_MAIN,
        }
      : {},
  }),
  breadcrumbArrow: (theme: Theme) => ({
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
  }),
  clientIdentity: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  userAvatarContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  userAvatar: (theme: Theme) => ({
    width: 24,
    height: 24,
    backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
    fontSize: "14px",
    fontFamily: "var(--font-Bold)",
  }),
  userAvatarName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
  }),
  userAvatarArrow: (theme: Theme) => ({
    fontSize: "16px",
    color: theme.colors_ds.PRIMARY_ICON_MAIN,
  }),
};
